import { PATH_API } from "constants/api";
import {
  API_GET_CACHE,
  API_HOST_PREFIX,
  DATA_STORE,
  Operation,
  StoreName,
} from "constants/serviceWorker";
import { ApiResponse } from "interfaces/models/api";
import {
  CreatePinList,
  GetPinReq,
  Pin,
  PinContent,
  PinDetail,
  UpdatePinGroup,
  UpdatePinGroupAction,
} from "interfaces/models/pin";
import { iCachedItem } from "interfaces/models/serviceWorker";
import { addToCache, matchToCache } from "libs/service-worker/cache";
import { isUndefined } from "lodash-es";
import { ChangedResponseData } from "../../BaseRequestServiceWorker";
import BaseCustomGetResponse from "./BaseCustomGetResponse";

const NUMBER_REQUEST_GET_LIST = 2; //get pin by inspection types, get pin by group
const NUMBER_REQUEST_GET_DETAIL = 1; // get detail
const TOTAL_NUMBER_REQUEST_GET =
  NUMBER_REQUEST_GET_LIST + NUMBER_REQUEST_GET_DETAIL;

/**
 * Flow sync data:
 * 1. Check is sync data by key pushed
 * 2. Merge data
 * 3. Update Key pushed
 */

class CustomResponseInspectionPin extends BaseCustomGetResponse {
  protected defaultNumberRequestRelative: number = TOTAL_NUMBER_REQUEST_GET;

  async getCachedItemByPins() {
    const params: GetPinReq = this.params as any;
    const indexedDb = await this.getIndexedDb();

    const [cachedContentItems, _cachedItems, cachedGroupItems] =
      await Promise.all([
        indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_PIN_CONTENT),
        indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_PIN),
        indexedDb?.getAll<UpdatePinGroup>(
          DATA_STORE,
          StoreName.INSPECTION_PIN_GROUP
        ),
      ]);
    const cachedCreateItems: iCachedItem<CreatePinList>[] = [];
    const cachedItems: iCachedItem<any>[] = [];
    _cachedItems.forEach((item) => {
      if (item.operation === Operation.Post) {
        const _item = item as iCachedItem<CreatePinList>;
        // We must filter correct request for prevent key pushed to cached data not correct
        if (_item.data.inspectionTypeId === params?.inspectionTypeId) {
          cachedCreateItems.push(item);
        } else if (_item.data.pinGroupId === params?.inspectionGroupId) {
          cachedCreateItems.push(item);
        }
      } else {
        cachedItems.push(item);
      }
    });

    return {
      cachedContentItems,
      cachedGroupItems,
      cachedItems,
      cachedCreateItems,
    };
  }

  /**
   *
   *   --- Flow sync data for getPinList  ---
   *  1. when add new pin
   *  - add pin into pinList
   *  2. when delete , update pin
   *  - remove pin in pinList and pin added, pinContent in IndexedDb
   *  - when edit in right sidebar
   *  3 when add, remove, sort pin group
   *  - update pinGroups of pin in pinList
   */
  async changeResponseListData(): Promise<ChangedResponseData> {
    // Get the API response data in a single destructuring operation
    const { apiResponse: _apiResponse, response } = await this.getResponse();
    const params: GetPinReq = this.params as any;
    const apiResponse = _apiResponse as ApiResponse<Pin[]>;
    const isLastPage = this.isLastPage(apiResponse, params);
    const isOffline = !this.swOnlineStatus;

    // Get all cached data in a single operation to avoid multiple async calls
    const { cachedItems, cachedGroupItems, cachedCreateItems } =
      await this.getCachedItemByPins();

    // Use Map instead of object for better performance with frequent lookups
    const mapData = new Map<string, Pin>();

    // Pre-populate the map with initial data
    apiResponse.data.forEach((pin) => mapData.set(pin.id, pin));

    // Create a Set of deleted item IDs for O(1) lookups
    const deleteItemIdsSet = new Set(
      cachedItems
        .filter((item) => item.operation === Operation.Delete)
        .map((item) => item.data as string)
    );

    // Process createItems first - handle existing and new items separately
    cachedCreateItems.forEach((cachedItem) => {
      const pins = cachedItem.data.pins as Pin[];
      pins.forEach((pin) => {
        // Check if item already exists in the map - O(1) operation
        if (mapData.has(pin.id)) {
          // Always update existing items regardless of page or online status
          mapData.set(
            pin.id,
            this.overrideCachedData({
              cachedItem: pin as any,
              currentItem: mapData.get(pin.id) as any,
              status: cachedItem.status,
            })
          );
        } else if (isLastPage && isOffline) {
          // Only add new items if on the last page and offline
          // And not marked for deletion
          if (!deleteItemIdsSet.has(pin.id)) {
            mapData.set(pin.id, pin);
          }
        }
      });
    });

    // Handle group-specific request data once before processing all items
    if (cachedGroupItems.length && this.params.inspectionGroupId) {
      cachedGroupItems.forEach((item) => {
        if (item.data.action === UpdatePinGroupAction.ADD) {
          const mapOfflineData = item.data.mapOfflineData;
          if (mapOfflineData) {
            // Add all pins in a single iteration
            item.data.pinIds.forEach((id) => {
              if (mapOfflineData[id] && !deleteItemIdsSet.has(id)) {
                mapData.set(id, mapOfflineData[id]);
              }
            });
          }
        }
      });
    }

    // Combine and sort all cached items once - avoid spread operator for large arrays
    const combinedCachedItems = new Array(
      cachedItems.length + cachedGroupItems.length
    );
    let index = 0;

    for (const item of cachedItems) combinedCachedItems[index++] = item;
    for (const item of cachedGroupItems) combinedCachedItems[index++] = item;

    // Sort in-place
    combinedCachedItems.sort((a, b) => a.requestTime - b.requestTime);

    // Type guards to ensure type safety when processing different item types
    function isPinCacheItem(item: iCachedItem<any>): item is iCachedItem<Pin> {
      return item.store === StoreName.INSPECTION_PIN;
    }

    function isPinGroupCacheItem(
      item: iCachedItem<any>
    ): item is iCachedItem<UpdatePinGroup> {
      return item.store === StoreName.INSPECTION_PIN_GROUP;
    }

    // Process each item with proper type checking
    for (const cachedItem of combinedCachedItems) {
      // Process pins with proper type enforcement
      if (isPinCacheItem(cachedItem)) {
        if (cachedItem.operation === Operation.Patch) {
          const pinData = cachedItem.data as Pin;
          // Skip processing if item is going to be deleted anyway
          if (!deleteItemIdsSet.has(pinData.id) && mapData.has(pinData.id)) {
            mapData.set(
              pinData.id,
              this.overrideCachedData({
                cachedItem: pinData,
                currentItem: mapData.get(pinData.id),
                status: cachedItem.status,
              })
            );
          }
        } else if (cachedItem.operation === Operation.Delete) {
          const itemId = cachedItem.data as unknown as string;
          const existed = mapData.has(itemId);
          // Match original behavior - only process if item exists or we're on last page, and we're offline
          if ((existed || isLastPage) && isOffline) {
            // Delete all items in the deleteItemIdsSet to match original behavior
            deleteItemIdsSet.forEach((id) => {
              mapData.delete(id);
            });
          }
        }
      }

      // Process pin groups with proper type enforcement
      else if (
        isPinGroupCacheItem(cachedItem) &&
        cachedItem.operation === Operation.Patch
      ) {
        const groupData = cachedItem.data as UpdatePinGroup;
        const { action, pinGroupId, order } = groupData;

        // Process all affected pins in a single loop
        (groupData.pinIds || []).forEach((pinId) => {
          const pin = mapData.get(pinId);
          if (!pin) return;

          // Initialize pinGroups if needed - avoid multiple checks
          if (!pin.pinGroups) {
            pin.pinGroups = [];
          }

          // Apply group action based on type
          switch (action) {
            case UpdatePinGroupAction.REMOVE:
              // Use filter once - more efficient than splice for multiple items
              pin.pinGroups = pin.pinGroups.filter(
                (i) => pinGroupId !== i.pinGroupId
              );
              break;

            case UpdatePinGroupAction.ADD: {
              // Use findIndex for a single iteration
              const existingIndex = pin.pinGroups.findIndex(
                (p) => p.pinGroupId === pinGroupId
              );
              if (existingIndex === -1) {
                // Only push if not found
                pin.pinGroups.push({
                  order: order ?? Date.now(),
                  pinGroupId,
                });
              }
              break;
            }

            case UpdatePinGroupAction.CHANGE_ORDER: {
              if (isUndefined(order)) break;

              // Use findIndex for a single traversal
              const indexGroup = pin.pinGroups.findIndex(
                (item) => item.pinGroupId === pinGroupId
              );
              if (indexGroup !== -1) {
                pin.pinGroups[indexGroup].order = order;
              }
              break;
            }
          }
        });
      }
    }

    // Convert Map to array - faster than Object.values for large datasets
    apiResponse.data = Array.from(mapData.values());

    return { data: apiResponse, response };
  }

  async getCachedItemByPin() {
    const indexedDb = await this.getIndexedDb();
    const [cachePinItems, cachedContentItems] = await Promise.all([
      indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_PIN),
      indexedDb?.getAll(DATA_STORE, StoreName.INSPECTION_PIN_CONTENT),
    ]);
    const cachedEditItems: iCachedItem<any>[] = [];
    const cachedCreateItems: iCachedItem<any>[] = [];
    cachePinItems?.forEach((item) => {
      if (item.operation === Operation.Patch) {
        cachedEditItems.push(item);
      } else if (item.operation === Operation.Post) {
        cachedCreateItems.push(item);
      }
    });

    return {
      cachedEditItems,
      cachedContentItems,
      cachedCreateItems,
    };
  }

  /**
   *
   *   --- Flow sync data for getPinDetail  ---
   *  1. when edit pin content in right sidebar
   *  - update data from cached
   *  2. when add new pin
   *  - add new data from pin added
   *  3. when edit pinContent
   *  - update pinContents of pin
   *  4. when delete pin
   *  - pinContent will deleted when sync data for getPinList
   */
  async changeResponseDetailData(): Promise<ChangedResponseData> {
    const { apiResponse, response } = await this.getResponse();
    const objectUrl = new URL(this.event.request.clone().url);
    const pinId = objectUrl.pathname.split("/").pop() || "";
    let data: PinDetail = Array.isArray(apiResponse.data)
      ? {}
      : apiResponse.data;

    if (!data) {
      return { data: apiResponse, response };
    }

    if (!data?.pinContents) {
      data.pinContents = [];
    }

    const { cachedEditItems, cachedContentItems, cachedCreateItems } =
      await this.getCachedItemByPin();

    // case sync get pin detail from add new pin
    for (const cachedItem of cachedCreateItems) {
      const pins = cachedItem.data.pins as Pin[];
      // eslint-disable-next-line
      pins.forEach((pin) => {
        if (pin.id === pinId) {
          data = this.overrideCachedData({
            cachedItem: pin as any,
            currentItem: data as any,
            status: cachedItem.status,
          });

          return;
        }
      });
    }

    // case sync get pin detail from edit pin
    for await (const cachedItem of cachedEditItems) {
      if (cachedItem.data.id === data?.id) {
        data = this.overrideCachedData({
          cachedItem: cachedItem.data,
          currentItem: data,
          status: cachedItem.status,
        });
      }
    }

    // case sync get pin contents of pin detail from edit pin
    cachedContentItems.forEach((cached) => {
      const item = cached.data as PinContent;
      const { itemResultId, pinId } = item;
      if (pinId !== data?.id) return cached;
      const index = (data?.pinContents || [])?.findIndex(
        (pinContent: PinContent) => pinContent.itemResultId === itemResultId
      );
      if (index === -1) {
        data.pinContents!.push(item);
      } else {
        data.pinContents![index] = item;
      }
    });
    apiResponse.data = data;

    return { data: apiResponse, response };
  }

  async changeResponseData(): Promise<Response> {
    const isArray = !!this.params?.paging;
    let response: ChangedResponseData;
    if (isArray) {
      // Change response data for get list
      response = await this.changeResponseListData();
    } else {
      response = await this.changeResponseDetailData();
    }

    return this.response(response);
  }
}

// Handle update cache after update pin group
export const updateCacheAfterUpdatePinGroup = async (
  params: {
    pin?: Pin;
  } & UpdatePinGroup
) => {
  const { pinGroupId, action, pin } = params;
  const url = `/${API_HOST_PREFIX}${PATH_API.INSPECTION_PIN}`;
  const cache = await caches.open(API_GET_CACHE);
  const cacheKeys = await cache.keys();
  const requestPinGroups: Request[] = [];
  const allRequestInspectionTypes: Request[] = [];
  cacheKeys.forEach((req) => {
    const requestUrl = req.url;
    if (!requestUrl.includes(url)) {
      return;
    }
    if (requestUrl.includes(`inspectionGroupId=${pinGroupId}`)) {
      requestPinGroups.push(req);
    }
    if (requestUrl.includes(`inspectionTypeId=${pin?.inspectionTypeId}`)) {
      allRequestInspectionTypes.push(req);
    }
  });

  await Promise.all(
    requestPinGroups.map(async (req) => {
      const res = await matchToCache(API_GET_CACHE, req, cache);
      if (!res) {
        return;
      }
      const pinsRes: ApiResponse<Pin[]> = await res.json();
      switch (action) {
        case UpdatePinGroupAction.ADD: {
          if (!pin || pinsRes.pagination) {
            break;
          }
          const newPin = structuredClone(pin);
          newPin.pinGroups = newPin?.pinGroups ?? [];
          const existed = newPin.pinGroups?.some(
            (p) => p.pinGroupId === pinGroupId
          );
          if (!existed) {
            newPin.pinGroups.push({
              pinGroupId,
              order: params.order ?? Date.now(),
            });
          }
          if (!pinsRes.data.some((itm) => itm.id === newPin.id)) {
            pinsRes.data.push(newPin);
          }
          break;
        }
      }
      const newResponse = new Response(JSON.stringify(pinsRes), {
        headers: res?.headers,
        status: res?.status || 202,
        statusText: res?.statusText,
      });
      await addToCache(API_GET_CACHE, req, newResponse, cache);
    })
  );
};

export default CustomResponseInspectionPin;
